import type { PatientInfoPolicyFields } from "@ascleo/frontdesk-contracts/patient-info/policy";

/**
 * The patient-information categories in the one order every surface must use.
 *
 * The terminal renders category rows in this order, the settings companion lists them in this
 * order, and the SOL card echoes them in this order. Nothing derives an order from object key
 * iteration, which is why this constant exists rather than a `Object.keys` walk.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import { PATIENT_INFO_CATEGORY_ORDER } from "@ascleo/frontdesk-shared/patient-info";
 *
 * const first: "phone" = PATIENT_INFO_CATEGORY_ORDER[0];
 * ```
 */
export const PATIENT_INFO_CATEGORY_ORDER = ["phone", "email", "hausarzt"] as const;

/**
 * One configurable patient-information category.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import type { PatientInfoCategory } from "@ascleo/frontdesk-shared/patient-info";
 *
 * const category: PatientInfoCategory = "hausarzt";
 * ```
 */
export type PatientInfoCategory = (typeof PATIENT_INFO_CATEGORY_ORDER)[number];

/**
 * A policy split into what patients will see and what they will not.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import type { PatientInfoPolicyProjection } from "@ascleo/frontdesk-shared/patient-info";
 *
 * const projection: PatientInfoPolicyProjection = {
 *   enabled: ["phone"],
 *   disabled: ["email", "hausarzt"],
 * };
 * ```
 */
export interface PatientInfoPolicyProjection {
  readonly enabled: readonly PatientInfoCategory[];
  readonly disabled: readonly PatientInfoCategory[];
}

/**
 * Partition the configured categories into enabled and disabled, both in canonical order.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import { projectPatientInfoPolicy } from "@ascleo/frontdesk-shared/patient-info";
 *
 * projectPatientInfoPolicy({ phone: true, email: false, hausarzt: true });
 * // => { enabled: ["phone", "hausarzt"], disabled: ["email"] }
 * ```
 */
export function projectPatientInfoPolicy(
  fields: PatientInfoPolicyFields,
): PatientInfoPolicyProjection {
  const enabled: PatientInfoCategory[] = [];
  const disabled: PatientInfoCategory[] = [];
  for (const category of PATIENT_INFO_CATEGORY_ORDER) {
    if (fields[category]) enabled.push(category);
    else disabled.push(category);
  }
  return { enabled, disabled };
}

/**
 * The German label a patient sees for each category.
 *
 * These are the same words the terminal prints on its category rows, which is what the settings
 * sentence promises ("mit denselben Bezeichnungen wie am Terminal").
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import { PATIENT_INFO_CATEGORY_LABEL_DE } from "@ascleo/frontdesk-shared/patient-info";
 *
 * PATIENT_INFO_CATEGORY_LABEL_DE.email; // => "E-Mail"
 * ```
 */
export const PATIENT_INFO_CATEGORY_LABEL_DE: Readonly<Record<PatientInfoCategory, string>> = {
  phone: "Telefon",
  email: "E-Mail",
  hausarzt: "Hausarzt",
};

/**
 * Join category labels with German list grammar (`A`, `A und B`, `A, B und C`).
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import { formatPatientInfoCategoryListDe } from "@ascleo/frontdesk-shared/patient-info";
 *
 * formatPatientInfoCategoryListDe(["phone", "email", "hausarzt"]);
 * // => "Telefon, E-Mail und Hausarzt"
 * ```
 */
export function formatPatientInfoCategoryListDe(
  categories: readonly PatientInfoCategory[],
): string {
  const labels = categories.map((category) => PATIENT_INFO_CATEGORY_LABEL_DE[category]);
  const last = labels.pop();
  if (last === undefined) return "";
  if (labels.length === 0) return last;
  return `${labels.join(", ")} und ${last}`;
}

/**
 * The generated patient-view sentence for the FrontDesk+ settings companion.
 *
 * `positive` is `null` when no category is enabled: the shared module states what patients will
 * check, and it has nothing truthful to say about an empty selection. Surfaces render their own
 * off-state copy for that case rather than receiving an invented sentence here.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import { formatPatientInfoPolicySentenceDe } from "@ascleo/frontdesk-shared/patient-info";
 *
 * formatPatientInfoPolicySentenceDe({ phone: true, email: false, hausarzt: true });
 * // => {
 * //   positive: "Patient:innen prüfen Telefon und Hausarzt – in dieser Reihenfolge, mit denselben Bezeichnungen wie am Terminal.",
 * //   negatives: ["E-Mail wird nicht angezeigt."],
 * // }
 * ```
 */
export function formatPatientInfoPolicySentenceDe(fields: PatientInfoPolicyFields): {
  readonly positive: string | null;
  readonly negatives: readonly string[];
} {
  const { enabled, disabled } = projectPatientInfoPolicy(fields);
  const positive =
    enabled.length === 0
      ? null
      : `Patient:innen prüfen ${formatPatientInfoCategoryListDe(enabled)} – in dieser Reihenfolge, mit denselben Bezeichnungen wie am Terminal.`;
  const negatives = disabled.map(
    (category) => `${PATIENT_INFO_CATEGORY_LABEL_DE[category]} wird nicht angezeigt.`,
  );
  return { positive, negatives };
}

/**
 * What both surfaces say when the review is switched off.
 *
 * This is a *master-switch* statement, not a "no category selected" one. The distinction matters:
 * a practice that disables the review keeps its stored categories, so a surface that only looked at
 * the categories would cheerfully report that patients are checking their phone number while the
 * terminal shows them nothing at all.
 *
 * Shared rather than duplicated because the settings card (through i18n) and the SOL card (through
 * German constants — bridge-ui has no i18n) describe one server state through different stacks; a
 * test in each package asserts its surface renders exactly this.
 *
 * Copy note: no design artefact covers the off state, so this wording is provisional and flagged
 * for copy review in `decisions.md` (D6).
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import { PATIENT_INFO_REVIEW_OFF_SENTENCE_DE } from "@ascleo/frontdesk-shared/patient-info";
 *
 * PATIENT_INFO_REVIEW_OFF_SENTENCE_DE.startsWith("Die Prüfung ist ausgeschaltet."); // => true
 * ```
 */
export const PATIENT_INFO_REVIEW_OFF_SENTENCE_DE =
  "Die Prüfung ist ausgeschaltet. Patient:innen werden am Terminal nicht nach ihren Kontaktdaten gefragt.";

/**
 * The short "Aktuell: …" clause the SOL card echoes from server policy.
 *
 * Returns `null` when nothing is enabled, for the same reason as
 * {@link formatPatientInfoPolicySentenceDe}.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import { formatPatientInfoPolicyCurrentDe } from "@ascleo/frontdesk-shared/patient-info";
 *
 * formatPatientInfoPolicyCurrentDe({ phone: true, email: false, hausarzt: true });
 * // => "Telefon und Hausarzt"
 * ```
 */
export function formatPatientInfoPolicyCurrentDe(fields: PatientInfoPolicyFields): string | null {
  const { enabled } = projectPatientInfoPolicy(fields);
  return enabled.length === 0 ? null : formatPatientInfoCategoryListDe(enabled);
}

/**
 * The per-category consequence line shown under each settings switch.
 *
 * Ordinary phone numbers and email addresses are saved directly; protected entries and every
 * Hausarzt change route to reception. The wording differs per category on purpose (Nummern vs
 * Adressen) and is taken verbatim from the accepted design canvas.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import { PATIENT_INFO_CATEGORY_CONSEQUENCE_DE } from "@ascleo/frontdesk-shared/patient-info";
 *
 * PATIENT_INFO_CATEGORY_CONSEQUENCE_DE.hausarzt;
 * // => "Jede Änderung geht als Aufgabe an den Empfang – nichts wird direkt überschrieben."
 * ```
 */
export const PATIENT_INFO_CATEGORY_CONSEQUENCE_DE: Readonly<
  Record<PatientInfoCategory, string>
> = {
  phone:
    "Gewöhnliche Nummern ändern Patient:innen direkt. Geschützte Einträge prüft der Empfang.",
  email:
    "Gewöhnliche Adressen ändern Patient:innen direkt. Geschützte Einträge prüft der Empfang.",
  hausarzt: "Jede Änderung geht als Aufgabe an den Empfang – nichts wird direkt überschrieben.",
};

/**
 * Every field combination with the exact strings both surfaces must render.
 *
 * The FrontDesk+ settings test and the SOL panel test both import this table. That is the whole
 * mechanism preventing the two surfaces from drifting: they render through different stacks (i18n
 * vs hardcoded German), so agreement is proven by assertion rather than by shared render code.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import { PATIENT_INFO_POLICY_SENTENCE_FIXTURES } from "@ascleo/frontdesk-shared/patient-info";
 *
 * PATIENT_INFO_POLICY_SENTENCE_FIXTURES.length; // => 8
 * ```
 */
export const PATIENT_INFO_POLICY_SENTENCE_FIXTURES: readonly {
  readonly fields: PatientInfoPolicyFields;
  readonly positive: string | null;
  readonly negatives: readonly string[];
  readonly current: string | null;
}[] = [
  {
    fields: { phone: false, email: false, hausarzt: false },
    positive: null,
    negatives: [
      "Telefon wird nicht angezeigt.",
      "E-Mail wird nicht angezeigt.",
      "Hausarzt wird nicht angezeigt.",
    ],
    current: null,
  },
  {
    fields: { phone: true, email: false, hausarzt: false },
    positive:
      "Patient:innen prüfen Telefon – in dieser Reihenfolge, mit denselben Bezeichnungen wie am Terminal.",
    negatives: ["E-Mail wird nicht angezeigt.", "Hausarzt wird nicht angezeigt."],
    current: "Telefon",
  },
  {
    fields: { phone: false, email: true, hausarzt: false },
    positive:
      "Patient:innen prüfen E-Mail – in dieser Reihenfolge, mit denselben Bezeichnungen wie am Terminal.",
    negatives: ["Telefon wird nicht angezeigt.", "Hausarzt wird nicht angezeigt."],
    current: "E-Mail",
  },
  {
    fields: { phone: false, email: false, hausarzt: true },
    positive:
      "Patient:innen prüfen Hausarzt – in dieser Reihenfolge, mit denselben Bezeichnungen wie am Terminal.",
    negatives: ["Telefon wird nicht angezeigt.", "E-Mail wird nicht angezeigt."],
    current: "Hausarzt",
  },
  {
    fields: { phone: true, email: true, hausarzt: false },
    positive:
      "Patient:innen prüfen Telefon und E-Mail – in dieser Reihenfolge, mit denselben Bezeichnungen wie am Terminal.",
    negatives: ["Hausarzt wird nicht angezeigt."],
    current: "Telefon und E-Mail",
  },
  {
    fields: { phone: true, email: false, hausarzt: true },
    positive:
      "Patient:innen prüfen Telefon und Hausarzt – in dieser Reihenfolge, mit denselben Bezeichnungen wie am Terminal.",
    negatives: ["E-Mail wird nicht angezeigt."],
    current: "Telefon und Hausarzt",
  },
  {
    fields: { phone: false, email: true, hausarzt: true },
    positive:
      "Patient:innen prüfen E-Mail und Hausarzt – in dieser Reihenfolge, mit denselben Bezeichnungen wie am Terminal.",
    negatives: ["Telefon wird nicht angezeigt."],
    current: "E-Mail und Hausarzt",
  },
  {
    fields: { phone: true, email: true, hausarzt: true },
    positive:
      "Patient:innen prüfen Telefon, E-Mail und Hausarzt – in dieser Reihenfolge, mit denselben Bezeichnungen wie am Terminal.",
    negatives: [],
    current: "Telefon, E-Mail und Hausarzt",
  },
];
